import React, { useState, useEffect, useMemo, useContext } from "react";
import styled from "styled-components";

import { defaultMapConfig } from "../constants";

export const MapContext = React.createContext(null);

const MapBase = ({
  mapConfig,
  doors,
  gateways,
  children,
  width,
  height,
  connecting,
}) => {
  const mapContext = useMemo(
    () => ({
      ...defaultMapConfig,
      ...mapConfig,
    }),
    [mapConfig]
  );

  const mapColor =
    mapContext.colors[mapConfig.map_color] || mapConfig.map_color;
  const viewBox = useMemo(() => {
    const { width, height, xScaleFactor, yScaleFactor, xPadding, yPadding } =
      mapContext;
    return [
      -xPadding,
      -yPadding,
      height * xScaleFactor + 2 * xPadding, // width
      width * yScaleFactor + 2 * yPadding, // height
    ].join(" ");
  }, [mapContext]);

  return (
    <MapBaseWrapper>
      <MapContext.Provider value={mapContext}>
        <svg fill={mapColor} viewBox={viewBox} width={width} height={height}>
          {/* draw grid */}
          <MapGrid />
          {/* draw doors */}
          <Doors doors={doors} />
          {/* draw gateways */}
          <Gateways gateways={gateways} />
          {children}
          {connecting && <Connecting />}
        </svg>
      </MapContext.Provider>
    </MapBaseWrapper>
  );
};

const MapGrid = React.memo(() => {
  const {
    width,
    height,
    xScaleFactor,
    yScaleFactor,
    gridRowHeight,
    xPadding,
    yPadding,
  } = useContext(MapContext);

  const lines = useMemo(() => {
    const nrOfLines = width + 1;
    return Array(nrOfLines)
      .fill()
      .map((_, i) => {
        return i * gridRowHeight;
      });
  }, [width, gridRowHeight]);

  const gridWidth = height * xScaleFactor + xPadding;
  const gridHeight = width * yScaleFactor + yPadding;

  return (
    <g id="grid">
      {/* left border */}
      <path
        d={`M ${-xPadding},${-yPadding} L ${-xPadding},${gridHeight}`}
        fill="transparent"
        stroke="#E4E8ED"
        strokeWidth={0.045}
      />
      {/* right border */}
      <path
        d={`M ${gridWidth},${-yPadding}, L ${gridWidth}, ${gridHeight}`}
        fill="transparent"
        stroke="#E4E8ED"
        strokeWidth={0.045}
      />
      {/* draw horizonal lines */}
      {lines.map((y, i) => (
        <path
          key={`grid-line-${i + 1}`}
          fill="none"
          d={`M ${-xPadding},${y} L ${gridWidth},${y}`}
          stroke="#E4E8ED"
          strokeWidth={0.1}
        />
      ))}
    </g>
  );
});

const Doors = React.memo(({ doors }) => {
  const {
    door_height,
    door_width,
    colors,
    xScaleFactor,
    yScaleFactor,
    xPadding,
  } = useContext(MapContext);

  const doorHeight = door_height || 4;
  const doorWidth = (door_width * yScaleFactor) / 3;

  const fontSize = (doorHeight / 4).toFixed(2);
  const color = colors.black;

  return (
    <g id="doors">
      {doors.map((d) => {
        const textX =
          d.y < 15
            ? d.y * xScaleFactor - fontSize * 4
            : d.y * xScaleFactor + xPadding / 5;
        const textY = d.x * yScaleFactor + fontSize / 2.4;
        return (
          <g id={d.name} key={d.name}>
            <text
              x={textX}
              y={textY}
              fontSize={fontSize}
              fontWeight={600}
              fill={color}
            >
              {d.name}
            </text>
          </g>
        );
      })}
      {/* draw doors separators */}
      {doors.map((d, i) => {
        const lineX = d.y * xScaleFactor;
        const lineY = d.x * yScaleFactor;

        return (
          <g key={`grid-sep-${i + 1}`}>
            <path
              fill="none"
              d={`M ${lineX}, ${lineY - doorWidth} L ${lineX}, ${
                lineY + doorWidth
              }`}
              stroke="#B9C2C9"
              strokeWidth={0.08}
            />
          </g>
        );
      })}
    </g>
  );
});

const Gateways = React.memo(({ gateways }) => {
  const { gw_color, gw_w, colors, xScaleFactor, yScaleFactor } =
    useContext(MapContext);

  const color = colors[gw_color] || gw_color || colors.black;
  const radius = gw_w * 2;

  return (
    <g id="gateways">
      {gateways.map((g) => (
        <circle
          key={g.name}
          cx={g.y * xScaleFactor}
          cy={g.x * yScaleFactor}
          r={radius}
          fill={color}
        />
      ))}
    </g>
  );
});

const Connecting = React.memo(() => {
  const [dots, setDots] = useState("");
  const { width, height, xScaleFactor, yScaleFactor, colors } =
    useContext(MapContext);
  const fontSize = 1.4;

  useEffect(() => {
    const id = setInterval(() => {
      setDots((dots) => (dots.length === 3 ? "" : dots + "."));
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, []);

  return (
    <text
      id="connecting"
      x={(height * xScaleFactor) / 2 - fontSize * 3}
      y={(width * yScaleFactor * 3) / 7}
      fontSize={fontSize}
      fontWeight={500}
      fill={colors.grey}
    >
      Connecting{dots}
    </text>
  );
});

const MapBaseWrapper = styled.div`
  width: 100%;
  min-height: 100%;
  display: flex;
  justify-content: center;
`;

export default MapBase;
